// AUTOMAÇÕES: um webhook por marca, disparado a cada ligação. Espelha
// src/features/automations/{controller,schema,router}.ts do back.
//
// A API NUNCA devolve o segredo do header (`headerValue`): ele só existe no
// momento em que o usuário digita um valor novo, em create/update. Um PATCH sem
// `headerValue` PRESERVA o segredo salvo — mandar string vazia o apagaria.
//
// Três leituras que a mesma tela faz: a lista de automações, o CATÁLOGO de
// campos disponíveis (/fields, que é o vocabulário do corpo do POST) e o
// HISTÓRICO de entregas de uma automação (/deliveries, onde o gestor descobre
// sozinho por que o parceiro não recebeu). As três moram juntas porque
// `Automation.fields` só faz sentido contra o catálogo, e a entrega só faz
// sentido contra a automação que a produziu.
import { api, toApiError } from "./client.js";

export const AUTOMATION_EVENTS = ["call.completed", "call.transcribed"] as const;
export type AutomationEvent = (typeof AUTOMATION_EVENTS)[number];

export interface Automation {
  id: string;
  name: string;
  event: AutomationEvent;
  url: string;
  headerName: string;
  fields: string[];
  // Para onde cada campo marcado vai NO CORPO do POST:
  // { chave_do_catalogo: caminho_no_destino }, com ponto para aninhar
  // ("metadata.lead_name"). Campo ausente daqui sai com a própria chave — o
  // corpo plano de sempre. Não é segredo (ao contrário do headerValue): volta
  // na API para a tela reabrir a configuração.
  fieldTargets: Record<string, string>;
  dispositionIds: string[];
  enabled: boolean;
  createdAt: string;
  ultimaEntrega: {
    status: string;
    httpStatus: number | null;
    error: string | null;
    at: string;
  } | null;
}

export interface AutomationFieldGroup {
  group: string;
  // `exemplo` só vem para campo cujo valor NÃO é escalar (hoje, a transcrição
  // por falante, que é lista). A prévia do corpo usa ele em vez de "<rótulo>".
  fields: Array<{ key: string; label: string; exemplo?: unknown }>;
}

export interface AutomationDelivery {
  id: string;
  callId: string;
  status: string;
  httpStatus: number | null;
  attempt: number;
  error: string | null;
  requestBody: unknown;
  createdAt: string;
}

export async function getAutomations(): Promise<Automation[]> {
  const { data, error } = await api<{ automations: Automation[] }>("/v1/automations");
  if (error) throw toApiError("GET /v1/automations", error);
  return data.automations;
}

export async function getAutomationFields(): Promise<AutomationFieldGroup[]> {
  const { data, error } = await api<{ groups: AutomationFieldGroup[] }>("/v1/automations/fields");
  if (error) throw toApiError("GET /v1/automations/fields", error);
  return data.groups;
}

export async function createAutomation(input: {
  name: string;
  event: AutomationEvent;
  url: string;
  headerName: string;
  headerValue: string;
  fields: string[];
  fieldTargets?: Record<string, string>;
  dispositionIds?: string[];
  enabled?: boolean;
}): Promise<Automation> {
  const { data, error } = await api<Automation>("/v1/automations", {
    method: "POST",
    body: input,
  });
  if (error) throw toApiError("POST /v1/automations", error);
  return data;
}

// headerValue ausente PRESERVA o segredo atual — mandar só quando o usuário
// digitou um valor novo.
export async function updateAutomation(
  id: string,
  patch: Partial<{
    name: string;
    url: string;
    headerName: string;
    headerValue: string;
    fields: string[];
    fieldTargets: Record<string, string>;
    dispositionIds: string[];
    enabled: boolean;
  }>,
): Promise<Automation> {
  const { data, error } = await api<Automation>(`/v1/automations/${id}`, {
    method: "PATCH",
    body: patch,
  });
  if (error) throw toApiError("PATCH /v1/automations", error);
  return data;
}

export async function deleteAutomation(id: string): Promise<void> {
  const { error } = await api(`/v1/automations/${id}`, { method: "DELETE" });
  if (error) throw toApiError("DELETE /v1/automations", error);
}

export async function getAutomationDeliveries(
  id: string,
  opts: { status?: string; limit?: number } = {},
): Promise<AutomationDelivery[]> {
  const { data, error } = await api<{ deliveries: AutomationDelivery[] }>(
    `/v1/automations/${id}/deliveries`,
    { query: opts },
  );
  if (error) throw toApiError("GET /v1/automations/:id/deliveries", error);
  return data.deliveries;
}

export async function testAutomation(
  id: string,
): Promise<{ httpStatus: number } | { erro: string }> {
  const { data, error } = await api<{ httpStatus: number } | { erro: string }>(
    `/v1/automations/${id}/test`,
    { method: "POST" },
  );
  if (error) throw toApiError("POST /v1/automations/:id/test", error);
  return data;
}
